import { EventSourceService, MessagingService, StorageService } from '.';
import type {
  ChatConfig,
  Conversation,
  ConversationStatus,
  Message,
} from '../types';
import { generateUUID } from '../utils';

export interface ChatServiceConfig extends ChatConfig {
  eventSourceUrl?: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  // User verification options
  enableUserVerification?: boolean;
  identityTokenProvider?: () => Promise<string>;
}

export interface ChatServiceState {
  conversation: Conversation | null;
  messages: Message[];
  status: ConversationStatus;
  isConnected: boolean;
  error: Error | null;
  isUserVerified: boolean;
}

export type ChatServiceListener = (state: ChatServiceState) => void;

/**
 * ChatService - Central orchestrator for Salesforce messaging chat
 *
 * This service manages the entire chat lifecycle, coordinating between
 * MessagingService, EventSourceService, and StorageService to provide
 * a simple, unified interface for chat functionality.
 */
export class ChatService {
  private static instance: ChatService | null = null;

  private messagingService: MessagingService;

  private eventService: EventSourceService | null = null;

  private storageService: StorageService;

  private config: ChatServiceConfig;

  private state: ChatServiceState = {
    conversation: null,
    messages: [],
    status: 'idle',
    isConnected: false,
    error: null,
    isUserVerified: false,
  };

  private listeners: Set<ChatServiceListener> = new Set();

  private conversationId: string | null = null;

  private jwt: string | null = null;

  private constructor(config: ChatServiceConfig) {
    this.config = config;

    // eslint-disable-next-line no-console
    console.log('🏗️ ChatService constructor called with config:', {
      organizationId: !!config.organizationId,
      deploymentDeveloperName: !!config.deploymentDeveloperName,
      messagingUrl: !!config.messagingUrl,
      eventSourceUrl: !!config.eventSourceUrl,
      eventSourceUrlValue: config.eventSourceUrl,
    });

    this.messagingService = new MessagingService(config);

    // Initialize storage service with organization-specific prefix
    this.storageService = new StorageService({
      type: 'localStorage',
      prefix: `sf-chat-${config.organizationId}`,
    });

    // Initialize event source service if URL is provided
    if (config.eventSourceUrl) {
      // eslint-disable-next-line no-console
      console.log(
        '✅ Initializing EventSourceService with URL:',
        config.eventSourceUrl
      );
      this.eventService = new EventSourceService({
        url: config.eventSourceUrl,
        organizationId: config.organizationId,
        reconnectInterval: config.reconnectInterval || 5000,
        maxReconnectAttempts: config.maxReconnectAttempts || 3,
        heartbeatTimeout: 90000,
      });
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        '⚠️ No eventSourceUrl provided - EventSource will not be initialized'
      );
    }
  }

  /**
   * Get or create singleton instance
   */
  public static getInstance(config: ChatServiceConfig): ChatService {
    if (!ChatService.instance) {
      ChatService.instance = new ChatService(config);
    }
    return ChatService.instance;
  }

  /**
   * Reset singleton instance (useful for testing or config changes)
   */
  public static resetInstance(): void {
    if (ChatService.instance) {
      ChatService.instance.cleanup();
      ChatService.instance = null;
    }
  }

  /**
   * Initialize the chat service
   * This should be called once when the application starts
   */
  public async initialize(): Promise<void> {
    // Prevent multiple initializations
    if (this.state.status === 'connecting' || this.state.status === 'active') {
      // eslint-disable-next-line no-console
      console.log(
        '⚠️ ChatService already initializing or active, skipping initialization'
      );
      return;
    }

    try {
      this.updateState({ status: 'connecting' });

      // Check for existing session
      const existingToken = this.storageService.getAuthToken();
      const existingConversationId = this.storageService.getConversationId();

      // eslint-disable-next-line no-console
      console.log('🔍 Checking for existing session...', {
        hasToken: !!existingToken,
        hasConversationId: !!existingConversationId,
        conversationId: existingConversationId,
      });

      if (existingToken && existingConversationId) {
        // eslint-disable-next-line no-console
        console.log('🔄 Attempting to restore existing session...');

        // Restore existing session
        this.jwt = existingToken;
        this.conversationId = existingConversationId;

        try {
          await this.messagingService.authenticate(existingToken);
          // eslint-disable-next-line no-console
          console.log('✅ Successfully authenticated with existing token');

          // Try to restore conversation
          const conversations = await this.messagingService.listConversations();
          // eslint-disable-next-line no-console
          console.log('📋 Retrieved conversations:', conversations);

          // eslint-disable-next-line no-console
          console.log(
            '🔍 Looking for conversation with ID:',
            existingConversationId
          );
          // eslint-disable-next-line no-console
          console.log(
            '🔍 Available conversations:',
            conversations.conversations.map((conv) => ({
              id: conv.conversationId,
              status: conv.status,
              startTimestamp: conv.startTimestamp,
            }))
          );

          // First try to find active conversation
          let targetConversation = conversations.conversations.find(
            (conv) =>
              conv.conversationId === existingConversationId &&
              conv.status === 'active'
          );

          // eslint-disable-next-line no-console
          console.log(
            '🔍 Found conversation with status "active":',
            !!targetConversation
          );

          // If not found with 'active' status, try other possible statuses
          if (!targetConversation) {
            // Try 'open' status (common in Salesforce)
            targetConversation = conversations.conversations.find(
              (conv) =>
                conv.conversationId === existingConversationId &&
                conv.status === 'open'
            );
            // eslint-disable-next-line no-console
            console.log(
              '🔍 Found conversation with status "open":',
              !!targetConversation
            );
          }

          // If still not found, try any conversation with matching ID that's not closed
          if (!targetConversation) {
            targetConversation = conversations.conversations.find(
              (conv) =>
                conv.conversationId === existingConversationId &&
                conv.status !== 'closed' &&
                conv.status !== 'ended'
            );
            // eslint-disable-next-line no-console
            console.log(
              '🔍 Found conversation with non-closed status:',
              !!targetConversation,
              targetConversation?.status
            );
          }

          if (targetConversation) {
            // eslint-disable-next-line no-console
            console.log('✅ Found active conversation, loading messages...');

            // Load existing messages
            const entries = await this.messagingService.listConversationEntries(
              existingConversationId
            );
            // eslint-disable-next-line no-console
            console.log(`📨 Loaded ${entries.length} conversation entries`);

            const messages = this.transformEntriesToMessages(entries);
            // eslint-disable-next-line no-console
            console.log(`💬 Transformed to ${messages.length} messages`);

            const conversation: Conversation = {
              id: existingConversationId,
              status: 'active',
              createdAt: new Date(targetConversation.startTimestamp),
              updatedAt: new Date(),
              participants: [],
            };

            this.updateState({
              conversation,
              messages,
              status: 'active',
              isConnected: true,
            });

            // Set up event handlers for real-time updates
            await this.setupEventHandlers();

            // eslint-disable-next-line no-console
            console.log(
              `✅ Successfully restored conversation ${existingConversationId} with ${messages.length} messages`
            );
            return;
          }
          // eslint-disable-next-line no-console
          console.warn(
            '⚠️ No restorable conversation found with ID:',
            existingConversationId
          );
          // eslint-disable-next-line no-console
          console.warn(
            '⚠️ Available conversations:',
            conversations.conversations
          );
          // eslint-disable-next-line no-console
          console.warn('⚠️ Clearing session data and starting fresh');
          this.storageService.clearSession();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('❌ Failed to restore existing conversation:', error);
          // Clear invalid session data
          this.storageService.clearSession();
        }
      } else {
        // eslint-disable-next-line no-console
        console.log('ℹ️ No existing session found - starting fresh');
      }

      // No existing session or restoration failed
      this.updateState({ status: 'idle' });
      // eslint-disable-next-line no-console
      console.log('💬 Chat service initialized - ready for new conversation');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Chat service initialization failed:', error);
      this.updateState({
        status: 'error',
        error:
          error instanceof Error ? error : new Error('Initialization failed'),
      });
      throw error;
    }
  }

  /**
   * Set identity token for user verification
   * This should be called after receiving an onEmbeddedMessagingReady event
   * and before starting a conversation to enable verified user conversations.
   */
  public async setIdentityToken(identityToken: string): Promise<void> {
    try {
      await this.messagingService.setIdentityToken(identityToken);
      this.updateState({ isUserVerified: true });
      // eslint-disable-next-line no-console
      console.log('✅ Identity token set for user verification');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to set identity token:', error);
      throw error;
    }
  }

  /**
   * Clear session including identity token
   * This should be called when the user logs out
   */
  public async clearSession(): Promise<void> {
    try {
      await this.messagingService.clearSession();
      this.storageService.clearSession();
      this.updateState({
        conversation: null,
        messages: [],
        status: 'idle',
        isConnected: false,
        isUserVerified: false,
        error: null,
      });
      this.conversationId = null;
      this.jwt = null;
      // eslint-disable-next-line no-console
      console.log('✅ Session cleared');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to clear session:', error);
      throw error;
    }
  }

  /**
   * Start a new conversation
   */
  public async startConversation(
    routingAttributes?: Record<string, string>
  ): Promise<void> {
    try {
      this.updateState({ status: 'connecting', error: null });

      // Check if user verification is enabled and get appropriate access token
      let tokenResponse;
      if (
        this.config.enableUserVerification &&
        this.messagingService.hasIdentityToken()
      ) {
        // eslint-disable-next-line no-console
        console.log('🔐 Starting verified conversation with identity token');
        tokenResponse =
          await this.messagingService.getAuthenticatedAccessToken();
      } else if (
        this.config.enableUserVerification &&
        this.config.identityTokenProvider
      ) {
        // eslint-disable-next-line no-console
        console.log('🔐 Getting identity token from provider');
        const identityToken = await this.config.identityTokenProvider();
        await this.setIdentityToken(identityToken);
        tokenResponse =
          await this.messagingService.getAuthenticatedAccessToken();
      } else {
        // eslint-disable-next-line no-console
        console.log('🔓 Starting unauthenticated conversation');
        tokenResponse =
          await this.messagingService.getUnauthenticatedAccessToken();
      }

      this.jwt = tokenResponse.accessToken;
      this.storageService.setAuthToken(this.jwt);

      // Generate conversation ID and create conversation
      this.conversationId = generateUUID();
      this.storageService.setConversationId(this.conversationId);

      await this.messagingService.createConversation(
        this.conversationId,
        routingAttributes
      );

      // Create conversation object
      const conversation: Conversation = {
        id: this.conversationId,
        status: 'active',
        createdAt: new Date(),
        updatedAt: new Date(),
        participants: [],
      };

      this.updateState({
        conversation,
        messages: [],
        status: 'active',
        isConnected: true,
      });

      // Set up event handlers for real-time updates
      // eslint-disable-next-line no-console
      console.log('🔧 About to set up event handlers...');
      await this.setupEventHandlers();

      // eslint-disable-next-line no-console
      console.log(`✅ Started new conversation ${this.conversationId}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to start conversation:', error);
      this.updateState({
        status: 'error',
        error:
          error instanceof Error
            ? error
            : new Error('Failed to start conversation'),
      });
      throw error;
    }
  }

  /**
   * Send a message in the current conversation
   */
  public async sendMessage(content: string): Promise<void> {
    if (!this.conversationId) {
      throw new Error('No active conversation');
    }

    try {
      // Create optimistic message
      const tempMessage: Message = {
        id: `temp_${Date.now()}`,
        conversationId: this.conversationId,
        content,
        sender: 'customer',
        timestamp: new Date(),
        messageType: 'text',
        deliveryStatus: 'sending',
      };

      // Add optimistic message to state
      this.updateState({
        messages: [...this.state.messages, tempMessage],
      });

      // Send message via API
      await this.messagingService.sendTextMessage(this.conversationId, content);

      // Update message status to delivered
      this.updateState({
        messages: this.state.messages.map((msg) =>
          msg.id === tempMessage.id
            ? { ...msg, deliveryStatus: 'delivered' }
            : msg
        ),
      });
    } catch (error) {
      // Mark message as failed
      this.updateState({
        messages: this.state.messages.map((msg) =>
          msg.deliveryStatus === 'sending'
            ? { ...msg, deliveryStatus: 'failed' }
            : msg
        ),
      });
      throw error;
    }
  }

  /**
   * End the current conversation
   */
  public async endConversation(): Promise<void> {
    if (!this.conversationId) {
      return;
    }

    try {
      this.updateState({ status: 'connecting' });

      await this.messagingService.endConversation(this.conversationId);

      // Clean up
      this.cleanup();
      this.storageService.clearSession();

      this.updateState({
        conversation: null,
        messages: [],
        status: 'closed',
        isConnected: false,
      });

      // eslint-disable-next-line no-console
      console.log(`✅ Ended conversation ${this.conversationId}`);
      this.conversationId = null;
      this.jwt = null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to end conversation:', error);
      this.updateState({
        status: 'error',
        error:
          error instanceof Error
            ? error
            : new Error('Failed to end conversation'),
      });
      throw error;
    }
  }

  /**
   * Subscribe to state changes
   */
  public subscribe(listener: ChatServiceListener): () => void {
    this.listeners.add(listener);

    // Immediately call with current state
    listener(this.state);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Get current state
   */
  public getState(): ChatServiceState {
    return { ...this.state };
  }

  /**
   * Clean up resources
   */
  public cleanup(): void {
    if (this.eventService) {
      this.eventService.disconnect();
    }
    this.listeners.clear();
  }

  // Private methods

  private async setupEventHandlers(): Promise<void> {
    if (!this.eventService) {
      // eslint-disable-next-line no-console
      console.warn(
        '⚠️ No event source service configured - real-time updates disabled'
      );
      return;
    }

    // EventSourceService has its own connection guards, so we don't need to check here

    try {
      // eslint-disable-next-line no-console
      console.log(
        '🔧 Setting up EventSource with JWT:',
        !!this.jwt,
        'Conversation:',
        this.conversationId
      );

      // Update EventSource config with current JWT
      if (this.jwt) {
        this.eventService.updateConfig({
          jwt: this.jwt,
        });
      } else {
        // eslint-disable-next-line no-console
        console.error('❌ No JWT available for EventSource connection!');
        return;
      }

      this.eventService.addHandler((event) => {
        switch (event.type) {
          case 'messageReceived':
            this.handleNewMessage(event.payload);
            break;
          case 'conversationEnded':
            this.handleConversationEnd(event.payload);
            break;
          case 'participantChanged':
            this.handleParticipantChanged(event.payload);
            break;
          case 'error': {
            // eslint-disable-next-line no-console
            console.error('Event stream error:', event.payload);
            const errorMessage =
              typeof event.payload === 'object' &&
              event.payload !== null &&
              'error' in event.payload &&
              typeof (event.payload as any).error === 'string'
                ? (event.payload as any).error
                : 'Event stream error';
            this.updateState({
              error: new Error(errorMessage),
            });
            break;
          }
          default:
            // eslint-disable-next-line no-console
            console.log('ℹ️ Unhandled ChatService event type:', event.type);
        }
      });

      // eslint-disable-next-line no-console
      console.log('🚀 Attempting EventSource connection...');
      await this.eventService.connect();
      // eslint-disable-next-line no-console
      console.log('✅ Event handlers set up successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to set up event handlers:', error);
      // Don't throw - chat can still work without real-time events
    }
  }

  private handleNewMessage(messageData: any): void {
    try {
      // eslint-disable-next-line no-console
      console.log('📥 Handling new message from EventSource:', messageData);

      // Check if this message is for the current conversation
      if (messageData.conversationId !== this.conversationId) {
        // eslint-disable-next-line no-console
        console.log(
          `Ignoring message for different conversation: ${messageData.conversationId} vs ${this.conversationId}`
        );
        return;
      }

      // Parse the Salesforce message format
      let messageContent = '[No content]';
      let senderId = `msg_${Date.now()}`;
      let senderRole = 'Unknown';
      let messageTimestamp = Date.now();

      if (messageData.conversationEntry) {
        try {
          const payload = JSON.parse(
            messageData.conversationEntry.entryPayload || '{}'
          );

          // Extract content using the same logic as transformEntriesToMessages
          messageContent =
            payload.abstractMessage?.staticContent?.text || '[No content]';
          senderId =
            messageData.conversationEntry.identifier ||
            payload.id ||
            `msg_${Date.now()}`;
          senderRole = messageData.conversationEntry.sender?.role || 'Unknown';
          messageTimestamp =
            messageData.conversationEntry.transcriptedTimestamp ||
            messageData.conversationEntry.clientTimestamp ||
            Date.now();

          // eslint-disable-next-line no-console
          console.log('📝 Parsed real-time message:', {
            id: senderId,
            content: messageContent,
            sender: senderRole,
            timestamp: new Date(messageTimestamp).toISOString(),
          });
        } catch (parseError) {
          console.error('Failed to parse message payload:', parseError);
        }
      }

      const message: Message = {
        id: senderId,
        conversationId: this.conversationId!,
        content: messageContent,
        sender: senderRole === 'EndUser' ? 'customer' : 'agent',
        timestamp: new Date(messageTimestamp),
        messageType: 'text',
        deliveryStatus: 'delivered',
      };

      // Check for duplicates by ID
      const existingMessage = this.state.messages.find(
        (msg) => msg.id === message.id
      );
      if (existingMessage) {
        // eslint-disable-next-line no-console
        console.log('⚠️ Duplicate message ignored:', message.id);
        return;
      }

      // Check if this is a real-time confirmation of an optimistic message
      // Look for temporary messages with similar content and timestamp
      const optimisticMessageIndex = this.state.messages.findIndex(
        (msg) =>
          msg.id.startsWith('temp_') &&
          msg.content === message.content &&
          msg.sender === message.sender &&
          Math.abs(msg.timestamp.getTime() - message.timestamp.getTime()) < 5000 // Within 5 seconds
      );

      if (optimisticMessageIndex !== -1) {
        // eslint-disable-next-line no-console
        console.log('🔄 Replacing optimistic message with real-time message');
        const updatedMessages = [...this.state.messages];
        updatedMessages[optimisticMessageIndex] = message;
        this.updateState({
          messages: updatedMessages,
        });
      } else {
        // eslint-disable-next-line no-console
        console.log('✅ Adding new real-time message to state');
        this.updateState({
          messages: [...this.state.messages, message],
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to handle new message:', error);
    }
  }

  private handleConversationEnd(data: any): void {
    // eslint-disable-next-line no-console
    console.log('Conversation ended:', data.conversationId);
    this.updateState({
      conversation: this.state.conversation
        ? {
            ...this.state.conversation,
            status: 'closed',
          }
        : null,
      status: 'closed',
      isConnected: false,
    });
    this.storageService.clearSession();
  }

  private handleParticipantChanged(eventData: any): void {
    try {
      // Extract participant information from the event
      const participantInfo = eventData?.conversationEntry?.entryPayload
        ? JSON.parse(eventData.conversationEntry.entryPayload)
        : null;

      // The participant data is in an entries array
      if (participantInfo?.entries && Array.isArray(participantInfo.entries)) {
        participantInfo.entries.forEach((entry: any) => {
          const { participant } = entry;
          const { operation } = entry; // 'add' or 'remove'
          const { displayName } = entry; // Agent name is here

          // Only show system message for agent connections (not customer)
          if (participant?.role === 'Agent' && operation === 'add') {
            const agentName = displayName || 'Agent';

            // Create a system message for agent joining
            const systemMessage: Message = {
              id: `system_agent_joined_${Date.now()}`,
              conversationId: this.conversationId!,
              content: `${agentName} has joined the conversation`,
              sender: 'system',
              timestamp: new Date(),
              messageType: 'system',
              deliveryStatus: 'delivered',
            };

            // Add the system message to the chat
            this.updateState({
              messages: [...this.state.messages, systemMessage],
            });
          }
        });
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  private updateState(updates: Partial<ChatServiceState>): void {
    this.state = { ...this.state, ...updates };

    // Notify all listeners
    this.listeners.forEach((listener) => {
      try {
        listener(this.state);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in state listener:', error);
      }
    });
  }

  // eslint-disable-next-line class-methods-use-this
  private transformEntriesToMessages(entries: any[]): Message[] {
    // eslint-disable-next-line no-console
    console.log('🔄 Transforming conversation entries to messages...');

    const messages = entries
      .filter((entry) => {
        const isMessage = entry.conversationEntry?.entryType === 'Message';
        if (!isMessage) {
          // eslint-disable-next-line no-console
          console.log(
            '⏭️ Skipping non-message entry:',
            entry.conversationEntry?.entryType
          );
        }
        return isMessage;
      })
      .map((entry) => {
        try {
          const payload = JSON.parse(
            entry.conversationEntry.entryPayload || '{}'
          );

          // Extract message content from the correct Salesforce structure
          const messageContent =
            payload.abstractMessage?.staticContent?.text || '[No content]';
          const senderId = entry.conversationEntry.identifier || payload.id;
          const senderRole = entry.conversationEntry.sender?.role || 'Unknown';
          const messageTimestamp =
            entry.conversationEntry.transcriptedTimestamp ||
            entry.conversationEntry.clientTimestamp;

          // eslint-disable-next-line no-console
          console.log('📝 Processing message entry:', {
            id: senderId,
            sender: senderRole,
            messageType: payload.abstractMessage?.messageType,
            hasText: !!messageContent && messageContent !== '[No content]',
            content: messageContent,
            timestamp: new Date(messageTimestamp).toISOString(),
          });

          const message: Message = {
            id: senderId,
            conversationId: entry.conversationId,
            content: messageContent,
            sender: senderRole === 'EndUser' ? 'customer' : 'agent',
            timestamp: new Date(messageTimestamp),
            messageType: 'text',
            deliveryStatus: 'delivered',
          };

          return message;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('❌ Failed to transform message entry:', error, entry);
          return null;
        }
      })
      .filter((message): message is Message => message !== null)
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    // eslint-disable-next-line no-console
    console.log(`✅ Successfully transformed ${messages.length} messages`);
    return messages;
  }
}

export default ChatService;
